/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { type FC, useEffect, useRef, useState } from "react";
import {
  Chip,
  EnergyLabel,
  Flex,
  FormRow,
  Grid,
  NumberField,
  Select,
  Spacing,
  Text,
  type Timeout,
  useDebug,
  usePubSub,
} from "@huub-web/ui";
import {
  FormInputDisplayConfiguration,
  InputConfiguration,
} from "@huub-web/ui/services/inputs";
import { useTranslation } from "react-i18next";
import {
  SearchPropertySelect,
  type SearchPropertySelectProps,
} from "../../SearchPropertySelect/SearchPropertySelect";
import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import { BaseSlide, type BaseSlideProps } from "./BaseSlide";
import type {
  WalterLivingAddress,
  WalterLivingInformation,
} from "../../../services";
import { energyLabels, type EnergyLabelValue } from "../../../types";
import { twoColumnGridStyle } from "./BaseSlide.css";

export type NewHouseSlideProps = BaseSlideProps & {
  getSearchResults?: SearchPropertySelectProps["getSearchResults"];
  getListing?: (
    addressObjectID: string,
  ) => Promise<WalterLivingInformation | null>;
};

/**
 * New house slide component for the mortgage wizard
 * Handles optional property search and selection
 *
 * ⚠️ When adding/removing conditional fields, update filterMaximumMortgageWizardValues.ts
 */
export const NewHouseSlide: FC<NewHouseSlideProps> = ({
  getSearchResults,
  getListing,
}) => {
  const { t } = useTranslation();
  const { state, updateFormValues } = useMaximumMortgageWizard();
  const pubSub = usePubSub();
  const { log } = useDebug();
  const plurality = state.formValues.hasPartner ? "plural" : "singular";
  const buildType =
    state.formValues.houseInMind === "newly-build" ? "newBuild" : "existing";
  const propertySelectTimeoutRef = useRef<Timeout | null>(null);
  const loadedAddressRef = useRef<string | null>(null);

  // State management
  const [houseInformation, setHouseInformation] =
    useState<WalterLivingInformation | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [searchPropertyError, setSearchPropertyError] =
    useState<boolean>(false);

  const handlePropertySearch = () => {
    // Property search handler - currently no specific implementation needed
  };

  const handlePropertySelect = async (address: WalterLivingAddress | null) => {
    if (!address || !getListing) {
      return;
    }

    // Clear any existing timeout
    if (propertySelectTimeoutRef.current) {
      clearTimeout(propertySelectTimeoutRef.current);
    }

    setSearchPropertyError(false);
    setSelectedLoading(true);

    propertySelectTimeoutRef.current = setTimeout(() => {
      getListing(address.address_object_id)
        .then((houseInfo) => {
          log("cyan", ["NewHouseSlide", "handlePropertySelect"], houseInfo);

          if (!houseInfo) {
            pubSub.publish("snackbar", {
              body: t("maxMortgageWizard.newHouse.noInfo"),
              variant: "error",
            });
            setSelectedLoading(false);
            return;
          }

          // Update form values with house information
          const formUpdates: any = {
            walterAddressObjectID: address.address_object_id,
          };

          if (
            houseInfo.listing?.list_price &&
            houseInfo.listing?.list_price !== 0
          ) {
            log(
              "cyan",
              ["NewHouseSlide", "handlePropertySelect"],
              "Setting new house price",
              houseInfo.listing?.list_price,
            );
            formUpdates.newHousePrice =
              houseInfo.listing?.list_price.toString();
          }

          if (houseInfo.property?.energy_label) {
            if (
              energyLabels.includes(
                houseInfo.property
                  ?.energy_label as (typeof energyLabels)[number],
              )
            ) {
              log(
                "cyan",
                ["NewHouseSlide", "handlePropertySelect"],
                "Setting new house energy label",
                houseInfo.property?.energy_label,
              );
              formUpdates.newHouseEnergyLabel =
                houseInfo.property?.energy_label;
            } else {
              console.warn(
                "[NewHouseSlide] handlePropertySelect",
                "Invalid energy label",
                houseInfo.property?.energy_label,
              );
            }
          }

          updateFormValues(formUpdates);
          loadedAddressRef.current = address.address_object_id;
          setHouseInformation(houseInfo);
        })
        .catch(() => {
          // Add listing manually
          setHouseInformation({
            address,
            listing: { is_listed: false },
          });
          loadedAddressRef.current = address.address_object_id;
          updateFormValues({
            walterAddressObjectID: address.address_object_id,
            newHousePrice: "",
            newHouseEnergyLabel: undefined,
          });
        })
        .finally(() => {
          setSelectedLoading(false);
        });
    }, 250);
  };

  const handlePropertyDelete = () => {
    setHouseInformation(null);
    loadedAddressRef.current = null;
    updateFormValues({
      walterAddressObjectID: undefined,
      newHousePrice: "",
      newHouseEnergyLabel: undefined,
    });
  };

  // Reset house information when address changes to a different one
  useEffect(() => {
    if (
      state.formValues.walterAddressObjectID &&
      loadedAddressRef.current &&
      loadedAddressRef.current !== state.formValues.walterAddressObjectID
    ) {
      log(
        "cyan",
        ["NewHouseSlide", "useEffect"],
        "Address changed, resetting house information",
      );
      setHouseInformation(null);
      loadedAddressRef.current = null;
    } else if (!state.formValues.walterAddressObjectID) {
      // If address is cleared, also clear the state
      setHouseInformation(null);
      loadedAddressRef.current = null;
    }
  }, [state.formValues.walterAddressObjectID, log]);

  // Load house information on mount if walterAddressObjectID exists
  useEffect(() => {
    const loadHouseInformation = async () => {
      if (
        state.formValues.walterAddressObjectID &&
        getListing &&
        !houseInformation &&
        !selectedLoading &&
        loadedAddressRef.current !== state.formValues.walterAddressObjectID
      ) {
        log(
          "cyan",
          ["NewHouseSlide", "useEffect"],
          "Loading house information from walterAddressObjectID",
          state.formValues.walterAddressObjectID,
        );

        setSelectedLoading(true);
        setSearchPropertyError(false);

        try {
          const houseInfo = await getListing(
            state.formValues.walterAddressObjectID,
          );

          if (houseInfo) {
            log(
              "cyan",
              ["NewHouseSlide", "useEffect"],
              "Successfully loaded house information",
              houseInfo,
            );

            // Update form values with house information
            // Only update fields that are currently empty
            const formUpdates: any = {};

            if (
              houseInfo.listing?.list_price &&
              houseInfo.listing?.list_price !== 0 &&
              (!state.formValues.newHousePrice ||
                state.formValues.newHousePrice === "")
            ) {
              log(
                "cyan",
                ["NewHouseSlide", "useEffect"],
                "Setting new house price",
                houseInfo.listing?.list_price,
              );
              formUpdates.newHousePrice =
                houseInfo.listing?.list_price.toString();
            }

            if (
              houseInfo.property?.energy_label &&
              !state.formValues.newHouseEnergyLabel
            ) {
              if (
                energyLabels.includes(
                  houseInfo.property
                    ?.energy_label as (typeof energyLabels)[number],
                )
              ) {
                log(
                  "cyan",
                  ["NewHouseSlide", "useEffect"],
                  "Setting new house energy label",
                  houseInfo.property?.energy_label,
                );
                formUpdates.newHouseEnergyLabel =
                  houseInfo.property?.energy_label;
              } else {
                console.warn(
                  "[NewHouseSlide] useEffect",
                  "Invalid energy label",
                  houseInfo.property?.energy_label,
                );
              }
            }

            // Update form values if we have any updates
            if (Object.keys(formUpdates).length > 0) {
              updateFormValues(formUpdates);
            }

            // Mark this address as loaded
            loadedAddressRef.current = state.formValues.walterAddressObjectID;
            setHouseInformation(houseInfo);
          } else {
            log(
              "yellow",
              ["NewHouseSlide", "useEffect"],
              "No house information found for walterAddressObjectID",
              state.formValues.walterAddressObjectID,
            );
            // Clear the walterAddressObjectID if no information is found
            updateFormValues({
              walterAddressObjectID: undefined,
              // newHousePrice: "",
              // newHouseEnergyLabel: undefined,
            });
          }
        } catch (error) {
          log(
            "red",
            ["NewHouseSlide", "useEffect"],
            "Failed to load house information",
            error,
          );
          setSearchPropertyError(true);
          // Clear the walterAddressObjectID on error
          updateFormValues({
            walterAddressObjectID: undefined,
            // newHousePrice: "",
            // newHouseEnergyLabel: undefined,
          });
        } finally {
          setSelectedLoading(false);
        }
      }
    };

    loadHouseInformation();
  }, [
    state.formValues.walterAddressObjectID,
    getListing,
    houseInformation,
    selectedLoading,
    log,
    updateFormValues,
  ]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (propertySelectTimeoutRef.current) {
        clearTimeout(propertySelectTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <BaseSlide
      slideKey="newHouse"
      title={t(`maxMortgageWizard.newHouse.title.${plurality}`)}
      description={t(
        `maxMortgageWizard.newHouse.description.${plurality}.${buildType}`,
      )}
      autoValidate
    >
      {state.formValues.purpose === "finance" &&
        state.formValues.houseProgress &&
        (state.formValues.houseInMind === "existing" ||
          state.formValues.houseInMind === "newly-build") && (
          <Flex direction="column" gap="m">
            <Grid variant="none" className={twoColumnGridStyle}>
              <FormRow
                id="wizard__newHousePrice"
                data-field-key="newHousePrice"
              >
                <NumberField
                  {...InputConfiguration.money.props}
                  leading={FormInputDisplayConfiguration.money.leading}
                  value={
                    state.formValues.newHousePrice &&
                    state.formValues.newHousePrice !== ""
                      ? Number(state.formValues.newHousePrice)
                      : undefined
                  }
                  onChange={(value) =>
                    updateFormValues({ newHousePrice: value?.toString() ?? "" })
                  }
                  validation={["required"]}
                  name={t("maxMortgageWizard.newHouse.askingPriceName")}
                  label={t(
                    state.formValues.houseInMind === "newly-build"
                      ? "maxMortgageWizard.newHouse.purchasePriceLabel"
                      : "maxMortgageWizard.newHouse.askingPriceLabel",
                  )}
                />
              </FormRow>
              <FormRow
                id="wizard__newHouseEnergyLabel"
                data-field-key="newHouseEnergyLabel"
              >
                <Select
                  value={state.formValues.newHouseEnergyLabel ?? ""}
                  onChange={(value) =>
                    updateFormValues({
                      newHouseEnergyLabel: value as
                        | "unknown"
                        | EnergyLabelValue,
                    })
                  }
                  options={[
                    {
                      label: t("maxMortgageWizard.newHouse.unknown"),
                      value: "unknown",
                    },
                    ...energyLabels.map((label) => ({
                      label: (
                        <EnergyLabel
                          label={label}
                          size="s"
                          justifySelf="flex-start"
                        />
                      ),
                      value: label,
                    })),
                  ]}
                  validation={["required"]}
                  name={t("maxMortgageWizard.newHouse.energyLabelName")}
                  label={t("maxMortgageWizard.newHouse.energyLabelLabel")}
                />
              </FormRow>
            </Grid>
          </Flex>
        )}

      {state.formValues.houseInMind !== "newly-build" && (
        <>
          <Spacing height="m" />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.newHouse.searchProperty")}
            </Text>
            <Text as="p" variant="bodyS" color="currentColor50">
              {t(
                `maxMortgageWizard.newHouse.searchPropertyDescription.${plurality}`,
              )}
            </Text>
          </Flex>

          <FormRow
            id="wizard__new_house"
            data-field-key="walterAddressObjectID"
          >
            {getSearchResults && (
              <SearchPropertySelect
                onSearch={handlePropertySearch}
                onChange={handlePropertySelect}
                onDelete={handlePropertyDelete}
                getSearchResults={getSearchResults}
                width="stretch"
                placeholder={t(
                  "maxMortgageWizard.newHouse.searchPropertyPlaceholder",
                )}
                noResultsText={t(
                  `maxMortgageWizard.newHouse.noResultsText.${plurality}`,
                )}
                selected={houseInformation ?? undefined}
                selectedLoading={selectedLoading}
                error={searchPropertyError}
              />
            )}

            {!getSearchResults && (
              <Chip variant="error">Property search functionality missing</Chip>
            )}
          </FormRow>
        </>
      )}

      {(state.formValues.purpose === "refinance" ||
        state.formValues.purpose === "renovate") && (
        <Flex direction="column" gap="m">
          <Spacing />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.newHouse.currentHomeValue")}
            </Text>
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseMarketValue"
              data-field-key="currentHouseMarketValue"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  state.formValues.currentHouseMarketValue &&
                  state.formValues.currentHouseMarketValue !== ""
                    ? Number(state.formValues.currentHouseMarketValue)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMarketValue: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.newHouse.marketValueName")}
                label={t("maxMortgageWizard.newHouse.marketValueLabel")}
              />
            </FormRow>

            <FormRow
              id="wizard__currentHouseEnergyLabel"
              data-field-key="currentHouseEnergyLabel"
            >
              <Select
                value={state.formValues.currentHouseEnergyLabel ?? ""}
                onChange={(value) =>
                  updateFormValues({
                    currentHouseEnergyLabel: value as
                      | "unknown"
                      | EnergyLabelValue,
                  })
                }
                options={[
                  {
                    label: t("maxMortgageWizard.newHouse.unknown"),
                    value: "unknown",
                  },
                  ...energyLabels.map((label) => ({
                    label: (
                      <EnergyLabel
                        label={label}
                        size="s"
                        justifySelf="flex-start"
                      />
                    ),
                    value: label,
                  })),
                ]}
                validation={["required"]}
                name={t("maxMortgageWizard.newHouse.energyLabelName")}
                label={t("maxMortgageWizard.newHouse.energyLabelLabel")}
              />
            </FormRow>
          </Grid>

          <Spacing />

          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.newHouse.currentMortgageDebt")}
            </Text>
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseMortgageDebt"
              data-field-key="currentHouseMortgageDebt"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  state.formValues.currentHouseMortgageDebt &&
                  state.formValues.currentHouseMortgageDebt !== ""
                    ? Number(state.formValues.currentHouseMortgageDebt)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMortgageDebt: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.newHouse.remainingMortgageName")}
                label={t("maxMortgageWizard.newHouse.remainingMortgageLabel")}
              />
            </FormRow>
          </Grid>
        </Flex>
      )}
    </BaseSlide>
  );
};
